<template>
  <div ref="stickyRoot" class="lcu-sticky-host">
    <slot />
  </div>
</template>

<script>
export default {
  name: 'sticky-element',
  props: {
    stickyClass: {
      type: String,
      required: true,
    },
    bottomOffset: {
      type: Number,
      default: 10,
    },
    stickyThreshold: {
      type: Number,
      default: 20,
    },
    releaseThreshold: {
      type: Number,
      default: 60,
    },
  },
  data() {
    return {
      stickyTarget: null,
      stickyPlaceholder: null,
      stickyRafId: null,
      isStickyActive: false,
    };
  },
  mounted() {
    this.$nextTick(() => {
      this.setupStickyTarget();
      this.updateStickyTarget();
    });
    window.addEventListener('resize', this.onStickyViewportChange);
    window.addEventListener('scroll', this.onStickyViewportChange, { passive: true });
  },
  updated() {
    this.$nextTick(() => {
      this.setupStickyTarget();
      this.updateStickyTarget();
    });
  },
  beforeUnmount() {
    this.teardownStickyTarget();
    window.removeEventListener('resize', this.onStickyViewportChange);
    window.removeEventListener('scroll', this.onStickyViewportChange);
    if (this.stickyRafId) {
      window.cancelAnimationFrame(this.stickyRafId);
      this.stickyRafId = null;
    }
  },
  methods: {
    onStickyViewportChange() {
      if (this.stickyRafId) {
        return;
      }
      this.stickyRafId = window.requestAnimationFrame(() => {
        this.stickyRafId = null;
        this.updateStickyTarget();
      });
    },
    getStickySelector() {
      return this.stickyClass.startsWith('.') ? this.stickyClass : `.${this.stickyClass}`;
    },
    setupStickyTarget() {
      const root = this.$refs.stickyRoot;
      if (!root || typeof root.querySelector !== 'function') {
        return;
      }
      const target = root.querySelector(this.getStickySelector());
      if (!target) {
        this.teardownStickyTarget();
        return;
      }
      if (this.stickyTarget === target && this.stickyPlaceholder) {
        return;
      }
      this.teardownStickyTarget();
      this.stickyTarget = target;
      this.stickyPlaceholder = document.createElement('div');
      this.stickyPlaceholder.className = 'lcu-sticky-placeholder';
      this.stickyPlaceholder.style.display = 'none';
      target.parentNode.insertBefore(this.stickyPlaceholder, target);
    },
    updateStickyTarget() {
      if (!this.stickyTarget || !this.stickyPlaceholder) {
        return;
      }
      this.stickyPlaceholder.style.display = 'block';
      this.stickyPlaceholder.style.height = `${this.stickyTarget.offsetHeight}px`;
      const bounds = this.stickyPlaceholder.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const shouldStick = this.isStickyActive
        ? bounds.bottom <= this.releaseThreshold
          || bounds.top >= viewportHeight - this.releaseThreshold
        : bounds.bottom <= this.stickyThreshold
          || bounds.top >= viewportHeight - this.stickyThreshold;
      this.isStickyActive = shouldStick;
      if (!this.isStickyActive) {
        this.stickyTarget.classList.remove('lcu-sticky-fixed');
        this.stickyTarget.style.width = '';
        this.stickyTarget.style.left = '';
        this.stickyTarget.style.bottom = '';
        this.stickyPlaceholder.style.display = 'none';
        this.stickyPlaceholder.style.height = '';
        return;
      }
      const { width, left } = this.stickyPlaceholder.getBoundingClientRect();
      this.stickyTarget.classList.add('lcu-sticky-fixed');
      this.stickyTarget.style.width = `${Math.round(width)}px`;
      this.stickyTarget.style.left = `${Math.round(left)}px`;
      this.stickyTarget.style.bottom = `${this.bottomOffset}px`;
    },
    teardownStickyTarget() {
      if (this.stickyTarget) {
        this.stickyTarget.classList.remove('lcu-sticky-fixed');
        this.stickyTarget.style.width = '';
        this.stickyTarget.style.left = '';
        this.stickyTarget.style.bottom = '';
      }
      if (this.stickyPlaceholder && this.stickyPlaceholder.parentNode) {
        this.stickyPlaceholder.parentNode.removeChild(this.stickyPlaceholder);
      }
      this.stickyTarget = null;
      this.stickyPlaceholder = null;
      this.isStickyActive = false;
    },
  },
};
</script>

<style scoped lang="scss">
.lcu-sticky-host {
  display: contents;
}

:deep(.lcu-sticky-buttons) {
  transition: box-shadow 0.2s ease, transform 0.2s ease;
}

:deep(.lcu-sticky-buttons.lcu-sticky-fixed) {
  position: fixed;
  z-index: 10;
  padding: 10px 15px;
  border: 1px solid $page-border;
  border-radius: 6px;
  background: rgba($white, 0.98);
  box-shadow: 0px 6px 18px rgba($black, 0.18);
  animation: lcu-sticky-enter 0.2s ease;
}

@keyframes lcu-sticky-enter {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
